import React, { useState, useEffect } from "react";
import { serverUrl } from "../../Utils/server";
import axios from "axios";
import { useParams } from "react-router";
import ReactLoading from "react-loading";
import { currencySymbol } from "../../Utils/currency";
import { toast, ToastContainer, darkToast } from "../../Components/Toast";
import FontAwesome from "react-fontawesome";

function ViewOrder() {
  const { orderId } = useParams();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState({});
  const [couriers, setCouriers] = useState([]);
  const orderItems = Array.isArray(order?.items) ? order.items : [];
  const safeCouriers = Array.isArray(couriers) ? couriers : [];
  const orderStatus = String(order?.status || "");
  const currentCourier = order?.courier || null;

  const monthMapEs = {
    jan: "01",
    ene: "01",
    feb: "02",
    mar: "03",
    apr: "04",
    abr: "04",
    may: "05",
    jun: "06",
    jul: "07",
    aug: "08",
    ago: "08",
    sep: "09",
    oct: "10",
    nov: "11",
    dec: "12",
    dic: "12",
  };

  const formatDateLatam = (value, fallback = "Fecha pendiente") => {
    if (!value) return fallback;

    const raw = String(value).trim();
    if (!raw) return fallback;

    const lower = raw.toLowerCase();
    if (lower === "any" || lower === "cualquiera") {
      return "Cualquiera";
    }

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
    }

    const latamMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (latamMatch) {
      return latamMatch[0];
    }

    const cleaned = raw
      .split("T")[0]
      .replace(/^(mon|tue|wed|thu|fri|sat|sun|lun|mar|mié|mie|jue|vie|sáb|sab|dom)\s*,\s*/i, "")
      .trim();

    const monthDayMatch = cleaned.match(/^([A-Za-zÁÉÍÓÚáéíóúñÑ]{3})\s+(\d{1,2})(?:,\s*(\d{4}))?$/i);

    if (monthDayMatch) {
      const month = monthMapEs[monthDayMatch[1].toLowerCase()];
      const day = monthDayMatch[2].padStart(2, "0");
      const year =
        monthDayMatch[3] ||
        (order?.date ? String(new Date(order.date).getFullYear()) : String(new Date().getFullYear()));

      if (month) {
        return `${day}/${month}/${year}`;
      }
    }

    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      const day = String(parsed.getDate()).padStart(2, "0");
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const year = String(parsed.getFullYear());
      return `${day}/${month}/${year}`;
    }

    return raw;
  };

  const statusLabel = (statusValue) => {
    const raw = String(statusValue || "").trim();
    const status = raw.toLowerCase();

    if (!raw) return "Sin estado";
    if (status.includes("cancelled by admin")) return "Cancelado por administrador";
    if (status.includes("order cancelled")) return "Cancelado por cliente";
    if (status.includes("cancelled") || status.includes("cancel")) return "Cancelado";
    if (status.includes("out for delivery")) return "En camino";
    if (status.includes("processing")) return "En preparación";
    if (status.includes("ordered")) return "Pedido realizado";
    if (status.includes("delivered")) return "Entregado";

    return raw;
  };

  const paymentMethodLabel = (paymentMethod) => {
    const raw = String(paymentMethod || "").trim();
    const method = raw.toLowerCase();

    if (!raw) return "No definido";
    if (method === "cash on delivery") return "Pago contra entrega";
    if (method === "online payment") return "Pago en línea";

    return raw;
  };

  const preferredDeliveryLabel = () => {
    const date = formatDateLatam(order?.preferredDeliveryDate, "Cualquiera");
    const time = String(order?.preferredDeliveryTime || "").trim();

    if ((date === "Cualquiera" || !date) && (!time || time.toLowerCase() === "any")) {
      return "Cualquiera";
    }

    return [date, time].filter(Boolean).join(" ");
  };


  useEffect(() => {
    setOrder({});
    setCouriers([]);
    getOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const getOrderDetails = () => {
    setLoading(true);

    const config = {
      method: "post",
      url: `${serverUrl}/orders/get-order-by-id`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.getItem("token")}`,
      },
      data: {
        orderObjId: orderId,
      },
    };

    axios(config)
      .then((response) => {
        setOrder(response.data && typeof response.data === "object" ? response.data : {});
        getCouriers();
      })
      .catch((err) => {
        toast("No se pudo cargar la información del pedido", darkToast);
        setLoading(false);
      });
  };

  const getCouriers = () => {
    const config = {
      method: "get",
      url: `${serverUrl}/users/couriers`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.getItem("token")}`,
      },
    };

    axios(config)
      .then((response) => {
        setCouriers(Array.isArray(response.data) ? response.data : []);
        setLoading(false);
      })
      .catch((err) => {
        toast("No se pudo cargar la información de repartidores", darkToast);
        setLoading(false);
      });
  };

  const updateOrderStatus = (status) => {
    setLoading(true);

    const config = {
      method: "post",
      url: `${serverUrl}/orders/update-status`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.getItem("token")}`,
      },
      data: {
        orderObjId: orderId,
        status: status,
      },
    };

    axios(config)
      .then((response) => {
        toast("Estado del pedido actualizado", darkToast);
        setLoading(false);
        window.location.reload();
      })
      .catch((err) => {
        toast("No se pudo actualizar el estado del pedido", darkToast);
        setLoading(false);
      });
  };

  const assignCourier = (courierIndex) => {
    if (courierIndex === "" || courierIndex === null || courierIndex === undefined) {
      return;
    }

    const selectedCourier = safeCouriers[Number(courierIndex)];
    const stableOrderId = order?._id || orderId;

    if (!stableOrderId || !selectedCourier) {
      toast("No se pudo preparar la asignación del repartidor", darkToast);
      return;
    }

    setLoading(true);

    const config = {
      method: "post",
      url: `${serverUrl}/orders/assign-courier`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.getItem("token")}`,
      },
      data: {
        orderId: stableOrderId,
        courier: selectedCourier,
      },
    };

    axios(config)
      .then((response) => {
        toast("Repartidor asignado", darkToast);
        setLoading(false);
        window.location.reload();
      })
      .catch((err) => {
        toast("No se pudo asignar el repartidor", darkToast);
        setLoading(false);
      });
  };

  const unassignCourier = () => {
    const stableOrderId = order?._id || orderId;

    if (!stableOrderId) {
      toast("No se pudo preparar la desasignación del repartidor", darkToast);
      return;
    }

    setLoading(true);

    const config = {
      method: "post",
      url: `${serverUrl}/orders/unassign-courier`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${localStorage.getItem("token")}`,
      },
      data: {
        orderId: stableOrderId,
      },
    };

    axios(config)
      .then((response) => {
        toast("Repartidor desasignado", darkToast);
        setLoading(false);
        window.location.reload();
      })
      .catch((err) => {
        toast("No se pudo desasignar el repartidor", darkToast);
        setLoading(false);
      });
  };

  return (
    <div className="content-body">
      <div className="content-title">
        <p>Detalles del pedido</p>
      </div>
      {loading ? (
        <ReactLoading
          type={"spinningBubbles"}
          color={"var(--primaryColor)"}
          width={"4rem"}
          height={"4rem"}
        />
      ) : (
        <div>
          <table className="order-details-table">
            {orderStatus.includes("Order Cancelled") ||
            orderStatus.includes("Delivered") ? (
              ""
            ) : (
              <>
                <tr>
                  <td>
                    <b>Actualizar estado</b>
                  </td>
                  <td>
                    <select
                      onChange={(e) => {
                        updateOrderStatus(e.target.value);
                        // toast("Can't update Order Status in Demo", darkToast);
                      }}
                    >
                      <option value="" defaultValue></option>
                      <option value="Processing">En preparación</option>
                      <option value="Out for delivery">En camino</option>
                      <option value="Cancelled">Cancelado</option>
                    </select>
                  </td>
                </tr>
                <tr>
                  <td>
                    <b>Repartidor</b>
                  </td>
                  <td>
                    {!currentCourier ? (
                      ""
                    ) : (
                      <>
                        <text
                          style={{
                            marginRight: "1rem",
                          }}
                        >
                          {currentCourier?.name || "Repartidor asignado"}
                        </text>
                        <button
                          onClick={() => {
                            unassignCourier();
                            // toast("Can't unasign Courier in Demo", darkToast);
                          }}
                          style={{
                            marginRight: "1rem",
                          }}
                        >
                          <FontAwesome name="trash" />
                        </button>
                      </>
                    )}
                    <select
                      onChange={(e) => {
                        assignCourier(e.target.value);
                        // toast("Can't assign Courier in Demo", darkToast);
                      }}
                    >
                      <option value="" defaultValue></option>
                      {safeCouriers.map((courier, index) => {
                        return <option key={courier?._id || index} value={index}>{courier?.name || "Sin nombre"}</option>;
                      })}
                    </select>
                  </td>
                </tr>
              </>
            )}
            <tr>
              <td>
                <b>ID del pedido</b>
              </td>
              <td>{order?.orderId || "-"}</td>
            </tr>
            <tr>
              <td>
                <b>Fecha del pedido</b>
              </td>
              <td>{formatDateLatam(order?.date)}</td>
            </tr>
            <tr>
              <td>
                <b>Cliente</b>
              </td>
              <td>{order?.clientName || "-"}</td>
            </tr>
            <tr>
              <td>
                <b>Productos</b>
              </td>
              <td>{orderItems.length}</td>
            </tr>
            <tr>
              <td>
                <b>Monto</b>
              </td>
              <td>
                {currencySymbol}
                {order?.totalPrice ?? 0}
              </td>
            </tr>
            <tr>
              <td>
                <b>Método de pago</b>
              </td>
              <td>{paymentMethodLabel(order?.paymentMethod)}</td>
            </tr>
            <tr>
              <td>
                <b>Estado</b>
              </td>
              <td>
                <span
                  className={`orderstatus ${
                    orderStatus.includes("Cancelled")
                      ? "cancelled "
                      : orderStatus.includes("Ordered")
                      ? "ordered "
                      : orderStatus.includes("Processing")
                      ? "processing "
                      : orderStatus.includes("Out for delivery")
                      ? "out-for-delivery "
                      : orderStatus.includes("Delivered")
                      ? "delivered "
                      : ""
                  }`}
                >
                  {statusLabel(orderStatus)}
                </span>
              </td>
            </tr>
            {orderStatus.includes("Delivered") && currentCourier ? (
              <tr>
                <td>
                  <b>Courier</b>
                </td>
                <td>{currentCourier?.name || "Repartidor asignado"}</td>
              </tr>
            ) : (
              ""
            )}
            <tr>
              <td>
                <b>Dirección de entrega</b>
              </td>
              <td>{order?.address || "-"}</td>
            </tr>
            <tr>
              <td>
                <b>Horario preferido de entrega</b>
              </td>
              <td>{preferredDeliveryLabel()}</td>
            </tr>
            <tr>
              <td>
                <b>Teléfono</b>
              </td>
              <td>{order?.phone || "-"}</td>
            </tr>
          </table>

          <hr />
          <br />
          <div className="content-title">
            <p>Productos del pedido</p>
          </div>
          <table>
            <tr>
              <th>No.</th>
              <th>Nombre</th>
              <th>Imagen</th>
              <th>Tipo</th>
              <th>Cantidad</th>
              <th>Precio</th>
              <th>Total</th>
            </tr>
            {orderItems.map((item, index) => {
              return (
                <tr>
                  <td>{index + 1}</td>
                  <td>{item.title}</td>
                  <td>
                    <img src={Array.isArray(item.images) && item.images.length > 0 ? item.images[0] : ""} alt="" />
                  </td>
                  <td>{item.isBundle ? "Paquete" : "Producto"}</td>
                  <td>{item.quantity}</td>
                  <td>
                    {currencySymbol}
                    {item.price}
                  </td>
                  <td>
                    {currencySymbol}
                    {item.price * item.quantity}
                  </td>
                </tr>
              );
            })}
            <tr>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>
                <b>Total bruto</b>
              </td>
              <td>
                <b>
                  {currencySymbol}
                  {order.totalPrice + order.discountAmount}
                </b>
              </td>
            </tr>
            <tr>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>
                <b>Descuento aplicado</b>
              </td>
              <td>
                <b>
                  -{currencySymbol}
                  {order.discountAmount}
                </b>
              </td>
            </tr>
            <tr>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>
                <b>Total final</b>
              </td>
              <td>
                <b>
                  {currencySymbol}
                  {order?.totalPrice ?? 0}
                </b>
              </td>
            </tr>
          </table>
        </div>
      )}
      <ToastContainer />
    </div>
  );
}

export default ViewOrder;
